import { useEffect, useRef, useState } from "react";
import WebViewer from "@pdftron/pdfjs-express-viewer";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@components/Button";
import {
  ArrowNarrowLeftIcon,
  DocumentIcon,
  DownloadIcon,
  LoaderIcon,
  MinusIcon,
  PlusIcon,
} from "@components/icons";
import type { IFile } from "@services/jointBillServices/types";
import {
  base64ToArrayBuffer,
  downloadFile,
  isNullOrUndefined,
} from "@utils/common";
import { DOCUMENT_VIEWER_LICENSE_KEY } from "@/constants";

interface IDocumentViewerProps {
  file?: IFile;
  onDocumentClose?: () => void;
  isLoading?: boolean;
  isPdf?: boolean;
}

/**
 * pdfjs-express-viewer does not support typescript.
 * If typescript is needed for modifying the viewer:
 * 1. import WebViewer from "@pdftron/webviewer";
 * 2. copy node_modules/@pdftron/webviewer/public contents to /public/pdftron
 * 3. change WebViewer path to "pdftron"
 */
export const DocumentViewer: React.FC<IDocumentViewerProps> = ({
  file,
  onDocumentClose,
  isLoading,
  isPdf,
}) => {
  const [maxPageNumber, setMaxPageNumber] = useState(0);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [isImageViewer, setIsImageViewer] = useState(false);
  const [instance, setInstance] = useState(null);
  const { t } = useTranslation();

  const viewer = useRef<HTMLDivElement>(null);
  const pageUpRef = useRef<HTMLButtonElement>(null);
  const pageDownRef = useRef<HTMLButtonElement>(null);

  /* eslint-disable */
  useEffect(() => {
    if (!file?.data || instance) {
      return;
    }

    WebViewer(
      {
        path: "/pdfjs",
        licenseKey: DOCUMENT_VIEWER_LICENSE_KEY,
        disabledElements: [
          "header",
          "pageNavOverlay",
          "toolsOverlay",
          "contextMenuPopup",
        ],
      },
      viewer.current,
      //@ts-expect-error -c
    ).then((inst) => {
      setInstance(inst);

      const { documentViewer } = inst.Core;
      const iframeDoc = inst.UI.iframeWindow.document;
      const FitMode = inst.UI.FitMode;

      iframeDoc
        ?.querySelector(".App")
        ?.setAttribute("style", "background: transparent;");

      documentViewer.addEventListener("documentLoaded", () => {
        const viewportWidth = iframeDoc?.querySelector(
          ".measurement-container",
        )?.clientWidth;
        const documentWidth =
          iframeDoc?.querySelector("#pageContainer1")?.clientWidth;
        if (viewportWidth <= 1366) {
          inst.UI.setFitMode(FitMode.FitWidth);
        } else {
          inst.UI.setZoomLevel(1000 / documentWidth);
        }

        setMaxPageNumber(documentViewer.getPageCount());

        if (pageUpRef.current) {
          pageUpRef.current.onclick = () =>
            documentViewer.setCurrentPage(
              documentViewer.getCurrentPage() + 1,
              false,
            );
        }

        if (pageDownRef.current) {
          pageDownRef.current.onclick = () =>
            documentViewer.setCurrentPage(
              documentViewer.getCurrentPage() - 1,
              false,
            );
        }
      });

      documentViewer.addEventListener(
        "pageNumberUpdated",
        (pageNumber: number) => setCurrentPageNumber(pageNumber),
      );
    });
  }, [file, instance]);
  /* eslint-enable */

  useEffect(() => {
    if (file?.data && (isPdf ?? file.contentType === "application/pdf")) {
      setIsImageViewer(false);
      //@ts-expect-error -cc
      // eslint-disable-next-line
      instance?.loadDocument(new Blob([base64ToArrayBuffer(file.data)]));
    } else {
      setIsImageViewer(true);
    }
  }, [isPdf, file, instance]);

  const closeDocument = () => {
    onDocumentClose?.();
    setIsImageViewer(false);
    setMaxPageNumber(0);
    setCurrentPageNumber(1);
  };

  return (
    <div
      className={twMerge(
        "pointer-events-auto fixed left-0 top-0 z-50 h-[100dvh] w-[100dvw] bg-base-black bg-opacity-80",
        !file?.data && "invisible h-0 w-0",
      )}
      onClick={() => closeDocument()}
    >
      <div
        ref={viewer}
        className={twMerge(
          "relative z-50 mt-[10vh] h-[80vh] w-full",
          isImageViewer && "invisible h-0 w-0",
        )}
      />
      {isImageViewer && file && (
        <div className="relative z-40 flex h-[80vh] w-full items-center justify-center">
          <img
            className="h-[80vh]"
            src={`data:${file.contentType};base64,` + file.data}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </div>
      )}
      <div className="absolute top-0 h-full w-full">
        <div className="relative flex h-full w-full items-center justify-center">
          <LoaderIcon
            className={twMerge(
              "z-50 hidden animate-spin",
              !isNullOrUndefined(isLoading) && isLoading && "block",
            )}
          />
          <div
            className="absolute left-4 top-4 flex items-center text-white"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Button icon={ArrowNarrowLeftIcon} onClick={closeDocument} />
            <div className="hidden md:flex">
              <DocumentIcon className="ml-4 mr-2" />
              <p>{file?.fileName}</p>
            </div>
          </div>
          {file && (
            <div className="absolute right-8 top-4">
              <Button
                icon={DownloadIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  downloadFile({
                    fileName: file.fileName,
                    data: file.data,
                  });
                }}
              >
                {t("documentViewer.buttons.download")}
              </Button>
            </div>
          )}
          <div
            className={twMerge(
              "absolute bottom-4 hidden w-full justify-center",
              maxPageNumber > 1 && "flex",
            )}
          >
            <div
              className="flex items-center gap-3"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <p className="rounded-full bg-blue-light px-4 py-2.5">
                {t("documentViewer.pageNumber", {
                  currentPageNumber,
                  maxPageNumber,
                })}
              </p>
              <Button icon={MinusIcon} ref={pageDownRef} />
              <Button icon={PlusIcon} ref={pageUpRef} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
